package com.swetdot.swetgram.ui.profile

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.Alignment
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextOverflow
import com.swetdot.swetgram.domain.LocalDay
import com.swetdot.swetgram.domain.ScheduledDeletion
import com.swetdot.swetgram.ui.chat.render
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.ContentWidth
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetCard
import com.swetdot.swetgram.ui.design.SwetDestructiveTextButton
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetSecondaryButton
import com.swetdot.swetgram.ui.design.SwetSkeleton
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.design.TouchTarget
import com.swetdot.swetgram.ui.moderation.canOpenModeration
import com.swetdot.swetgram.ui.text.resolve
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import kotlinx.datetime.TimeZone
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.profile_blocked_users_title
import swetgram.shared.generated.resources.profile_edit
import swetgram.shared.generated.resources.profile_privacy_title
import swetgram.shared.generated.resources.profile_settings
import swetgram.shared.generated.resources.profile_settings_about
import swetgram.shared.generated.resources.profile_settings_appearance
import swetgram.shared.generated.resources.profile_settings_chats
import swetgram.shared.generated.resources.profile_settings_delete_account
import swetgram.shared.generated.resources.profile_settings_delete_account_body
import swetgram.shared.generated.resources.profile_settings_delete_account_title
import swetgram.shared.generated.resources.profile_settings_deletion_on
import swetgram.shared.generated.resources.profile_settings_deletion_recovery
import swetgram.shared.generated.resources.profile_settings_deletion_scheduled
import swetgram.shared.generated.resources.profile_settings_deletion_undated
import swetgram.shared.generated.resources.profile_settings_email
import swetgram.shared.generated.resources.profile_settings_email_unverified
import swetgram.shared.generated.resources.profile_settings_email_verified
import swetgram.shared.generated.resources.profile_settings_keep_account
import swetgram.shared.generated.resources.profile_settings_language
import swetgram.shared.generated.resources.profile_settings_log_out
import swetgram.shared.generated.resources.profile_settings_log_out_body
import swetgram.shared.generated.resources.profile_settings_log_out_title
import swetgram.shared.generated.resources.profile_settings_moderation_reports
import swetgram.shared.generated.resources.profile_settings_notifications
import swetgram.shared.generated.resources.profile_settings_section_account
import swetgram.shared.generated.resources.profile_settings_section_app
import swetgram.shared.generated.resources.profile_settings_section_moderation
import swetgram.shared.generated.resources.profile_settings_section_privacy
import swetgram.shared.generated.resources.profile_settings_storage
import swetgram.shared.generated.resources.premium_settings_entry

/**
 * Settings -- SPS §17.1.
 *
 * The spec's recommended structure is Account, Privacy, Notifications, Appearance, Chats,
 * Storage, Language, About, Log Out. Two of those exist behind this build (Account and Privacy);
 * the rest have neither a screen nor an API behind them yet.
 *
 * So they are OPTIONAL PARAMETERS, and a section with no callback is not drawn. That follows the
 * precedent `ui/Navigation.kt` set for its placeholder tabs: the app would rather show less than
 * claim something works. A row that opens nothing, or opens a screen saying "coming soon", is a
 * dead end the user has to discover by tapping it; an absent row costs them nothing. Passing a
 * lambda is all it takes to light each one up.
 *
 * Notifications in particular is NOT missing for want of an endpoint -- the contract has
 * `getNotificationSettings` / `updateNotificationSettings` -- but nothing in `:shared` implements
 * them, and this change does not own the data layer.
 *
 * Account deletion is the one flow that lives entirely here. It is scheduled rather than
 * immediate: the contract gives a recovery window, and the window's length is DISPLAYED as the
 * server sent it rather than hardcoded, because "30 days" is a server policy and this screen
 * would be making a promise it cannot keep.
 */
@Composable
fun SettingsScreen(
    state: SettingsUiState,
    appVersionLabel: String,
    onBack: () -> Unit,
    onEditProfile: () -> Unit,
    onPrivacy: () -> Unit,
    onBlockedUsers: () -> Unit,
    onSignOut: () -> Unit,
    onRequestDeletion: () -> Unit,
    onCancelDeletion: () -> Unit,
    modifier: Modifier = Modifier,
    onRetry: (() -> Unit)? = null,
    onDismissError: (() -> Unit)? = null,
    onNotifications: (() -> Unit)? = null,
    onPremium: (() -> Unit)? = null,
    onAppearance: (() -> Unit)? = null,
    onChats: (() -> Unit)? = null,
    onStorage: (() -> Unit)? = null,
    onLanguage: (() -> Unit)? = null,
    onAbout: (() -> Unit)? = null,
    /**
     * The moderation queue, for staff only.
     *
     * TWO LOCKS, and both must be open before this is drawn: the host has to pass a callback, AND
     * `getMe` has to have ACTUALLY ANSWERED with `moderator` or `admin` -- see
     * [com.swetdot.swetgram.ui.moderation.canOpenModeration]. The second lock lives here rather
     * than at the call site precisely so that a host which wired this unconditionally still cannot
     * show a moderation entry to an ordinary account.
     *
     * INVISIBLE, NOT DISABLED. A greyed "Moderation" row would tell every user in the world that a
     * panel exists and that they are not allowed in; `GroupScreen` argues the same for its manage
     * rows, and SDS §25.1's disabled state is for something momentarily unavailable, which "you are
     * not a moderator" is not.
     *
     * It is a convenience, not a boundary. The server answers `403 admin_role_required` to
     * everybody else whatever this client draws.
     */
    onModeration: (() -> Unit)? = null,
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    var confirming by remember { mutableStateOf<SettingsConfirmation?>(null) }
    val uri = LocalUriHandler.current

    Column(modifier.fillMaxSize()) {
        SwetTopAppBar(title = stringResource(Res.string.profile_settings), onBack = onBack)

        if (state.error != null) {
            SwetErrorBanner(
                message = state.error.resolve(),
                onRetry = onRetry,
                onDismiss = onDismissError,
            )
        }

        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.TopCenter) {
            Column(
                modifier = Modifier
                .widthIn(max = ContentWidth.settings)
                .fillMaxSize()
                // A pushed detail route has no tab bar under it to carry this inset; see
                // `PrivacyScreen` for why it goes before `verticalScroll`.
                .navigationBarsPadding()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = Space.screenMargin),
            ) {
            // First, because it is the only thing on this screen with a deadline attached.
            state.deletion?.let { deletion ->
                Spacer(Modifier.height(Space.two))
                DeletionCard(
                    deletion = deletion,
                    busy = state.busy,
                    onCancel = onCancelDeletion,
                    zone = zone,
                )
            }

            ProfileSectionHeader(stringResource(Res.string.profile_settings_section_account))
            SwetCard(contentPadding = PaddingValues(vertical = Space.half)) {
                EmailRow(state = state)
                ProfileNavRow(
                    title = stringResource(Res.string.profile_edit),
                    onClick = onEditProfile,
                )
            }

            Spacer(Modifier.height(Space.two))

            ProfileSectionHeader(stringResource(Res.string.profile_settings_section_privacy))
            SwetCard(contentPadding = PaddingValues(vertical = Space.half)) {
                ProfileNavRow(
                    title = stringResource(Res.string.profile_privacy_title),
                    onClick = onPrivacy,
                )
                ProfileNavRow(
                    title = stringResource(Res.string.profile_blocked_users_title),
                    onClick = onBlockedUsers,
                )
            }

            // Staff only, and absent for everybody else -- see [onModeration]. It sits under
            // Privacy and safety because that is what it is, and above App because it is not one.
            if (onModeration != null && canOpenModeration(state.profile?.role)) {
                Spacer(Modifier.height(Space.two))
                ProfileSectionHeader(stringResource(Res.string.profile_settings_section_moderation))
                SwetCard(contentPadding = PaddingValues(vertical = Space.half)) {
                    ProfileNavRow(
                        title = stringResource(Res.string.profile_settings_moderation_reports),
                        onClick = onModeration,
                    )
                }
            }

            // Everything the spec asks for that this build can actually open. Drawn only if at
            // least one of them exists, so an empty card never appears under a header.
            //
            // The six labels are looked up UNCONDITIONALLY and paired afterwards, rather than
            // resolved inside each `?.let`. A `stringResource` inside a conditionally-executed
            // lambda is a composable call that sometimes happens and sometimes does not, which is
            // the shape that needs care; six lookups on a screen that draws at most six rows is
            // not worth being clever about.
            val notificationsLabel = stringResource(Res.string.profile_settings_notifications)
            val premiumLabel = stringResource(Res.string.premium_settings_entry)
            val appearanceLabel = stringResource(Res.string.profile_settings_appearance)
            val chatsLabel = stringResource(Res.string.profile_settings_chats)
            val storageLabel = stringResource(Res.string.profile_settings_storage)
            val languageLabel = stringResource(Res.string.profile_settings_language)
            val aboutLabel = stringResource(Res.string.profile_settings_about)
            val extras = listOfNotNull(
                onPremium?.let { premiumLabel to it },
                onNotifications?.let { notificationsLabel to it },
                onAppearance?.let { appearanceLabel to it },
                onChats?.let { chatsLabel to it },
                onStorage?.let { storageLabel to it },
                onLanguage?.let { languageLabel to it },
                onAbout?.let { aboutLabel to it },
            )
            if (extras.isNotEmpty()) {
                Spacer(Modifier.height(Space.two))
                ProfileSectionHeader(stringResource(Res.string.profile_settings_section_app))
                SwetCard(contentPadding = PaddingValues(vertical = Space.half)) {
                    extras.forEach { (label, action) ->
                        ProfileNavRow(title = label, onClick = action)
                    }
                }
            }

            Spacer(Modifier.height(Space.two))
            ProfileSectionHeader("About & Legal")
            SwetCard(contentPadding = PaddingValues(vertical = Space.half)) {
                ProfileControlRow(title = "Swetgram", supporting = appVersionLabel) { }
                ProfileNavRow(title = "Terms of Service", onClick = { uri.openUri("https://swetgram.com/terms") })
                ProfileNavRow(title = "Privacy Policy", onClick = { uri.openUri("https://swetgram.com/privacy") })
                ProfileNavRow(
                    title = "Community Guidelines",
                    onClick = { uri.openUri("https://swetgram.com/community-guidelines") },
                )
                ProfileNavRow(
                    title = "Account deletion information",
                    onClick = { uri.openUri("https://swetgram.com/account-deletion") },
                )
            }

            Spacer(Modifier.height(Space.four))

            SwetSecondaryButton(
                text = stringResource(Res.string.profile_settings_log_out),
                onClick = { confirming = SettingsConfirmation.SignOut },
                enabled = !state.busy,
            )

            Spacer(Modifier.height(Space.two))

            // Only offered while nothing is scheduled -- the card above owns the state once one
            // is, and two controls for one deletion is how a user ends up scheduling it twice.
            //
            // §11.1's TEXT destructive treatment, not the filled one. Filled, this was the loudest
            // element on the screen: the irreversible action drawn heavier than "Log out", which
            // is routine and undoable, so the first thing a new user's eye landed on was account
            // deletion. The filled variant still appears -- inside the confirmation dialog, where
            // destroying the account is the only thing being decided.
            if (state.deletion == null) {
                SwetDestructiveTextButton(
                    text = stringResource(Res.string.profile_settings_delete_account),
                    onClick = { confirming = SettingsConfirmation.DeleteAccount },
                    enabled = !state.busy,
                )
            }

            Spacer(Modifier.height(Space.six))
            }
        }
    }

    when (confirming) {
        null -> Unit

        SettingsConfirmation.SignOut -> ProfileConfirmDialog(
            title = stringResource(Res.string.profile_settings_log_out_title),
            body = stringResource(Res.string.profile_settings_log_out_body),
            confirmText = stringResource(Res.string.profile_settings_log_out),
            onConfirm = {
                confirming = null
                onSignOut()
            },
            onDismiss = { confirming = null },
            // Nothing is lost: the account, the messages and the groups are all still there.
            // §11.1 keeps `danger` for actions that destroy something.
            destructive = false,
        )

        SettingsConfirmation.DeleteAccount -> ProfileConfirmDialog(
            title = stringResource(Res.string.profile_settings_delete_account_title),
            // Deliberately does not name a number of days. The recovery window comes back with
            // the response, and stating it before we have it would be a guess printed as a fact
            // on the one screen where being wrong is unrecoverable. The resource carries that
            // decision now, so a translator cannot helpfully add "30" to it.
            body = stringResource(Res.string.profile_settings_delete_account_body),
            confirmText = stringResource(Res.string.profile_settings_delete_account),
            onConfirm = {
                confirming = null
                onRequestDeletion()
            },
            onDismiss = { confirming = null },
            busy = state.busy,
        )
    }
}

private enum class SettingsConfirmation { SignOut, DeleteAccount }

/**
 * The email row. Read-only: `requestEmailChange` needs a password re-entry and a confirmation
 * token, which is a flow of its own rather than a row on this screen.
 *
 * The verification state is the reason it is here at all -- an unverified account cannot send
 * messages, and this is the one place that fact is visible.
 */
@Composable
private fun EmailRow(state: SettingsUiState) {
    val colors = SwetgramThemeTokens.colors
    val profile = state.profile

    when {
        profile != null -> ProfileControlRow(
            title = stringResource(Res.string.profile_settings_email),
            supporting = profile.email,
        ) {
            Text(
                // TWO KEYS RATHER THAN ONE WORD AND A NEGATION. Russian agrees the participle with
                // the noun the row is titled by -- "Почта подтверждена", feminine -- so a single
                // shared "Verified" would be right beside this row and wrong beside the next noun
                // that borrowed it. The Russian file records the same argument at the keys.
                text = if (profile.isEmailVerified) {
                    stringResource(Res.string.profile_settings_email_verified)
                } else {
                    stringResource(Res.string.profile_settings_email_unverified)
                },
                style = MaterialTheme.typography.labelMedium,
                // §4.6's semantics in the only vocabulary a monochrome palette has: WEIGHT.
                // Neither is decoration -- the words say the same thing, so this is reinforcement
                // rather than the only signal (§22).
                //
                // AND THE EMPHASIS IS THE OTHER WAY ROUND NOW, deliberately. The green/amber pair
                // shouted both states equally: a settled account announced "Verified" as loudly as
                // an unsettled one announced "Not verified". Here the state with work left in it is
                // the bright one and the finished state recedes, which is what the reader needs
                // and what the two colours were never able to say.
                color = if (profile.isEmailVerified) colors.textSecondary else colors.textPrimary,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }

        // Still loading. A reserved block rather than a spinner, so the rows below do not jump
        // when the address arrives (SDS §2.1).
        !state.loadedOnce -> Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(TouchTarget.recommended)
                .padding(horizontal = Space.two, vertical = Space.one),
        ) {
            SwetSkeleton(
                Modifier
                    .width(Sizes.avatarProfile)
                    .height(Space.two),
            )
        }

        // Loaded and failed. The banner above already explains it; repeating "unavailable" in
        // the row would say the same thing twice.
        else -> Unit
    }
}

/**
 * The scheduled-deletion card.
 *
 * [ScheduledDeletion.scheduledFor] is nullable, and the two cases are worded differently on
 * purpose: showing a parse failure as a date would tell someone their account disappears at a
 * moment that is not true, on the screen where they are deciding whether to stop it.
 */
@Composable
private fun DeletionCard(
    deletion: ScheduledDeletion,
    busy: Boolean,
    onCancel: () -> Unit,
    zone: TimeZone,
) {
    val colors = SwetgramThemeTokens.colors

    SwetCard(showBorder = true) {
        Text(
            text = stringResource(Res.string.profile_settings_deletion_scheduled),
            style = MaterialTheme.typography.titleMedium,
            color = colors.danger,
        )
        Spacer(Modifier.height(Space.one))
        Text(
            // The DATE is an argument and the two cases are two keys, because they are two
            // different claims: one names a day, the other admits it cannot. `LocalDay.render()`
            // produces `dd.MM.yyyy`, which is right in both languages.
            text = deletion.scheduledFor
                ?.let {
                    stringResource(
                        Res.string.profile_settings_deletion_on,
                        LocalDay.of(it, zone).render(),
                    )
                }
                ?: stringResource(Res.string.profile_settings_deletion_undated),
            style = MaterialTheme.typography.bodyMedium,
            color = colors.textPrimary,
        )
        Spacer(Modifier.height(Space.half))
        Text(
            // A PLURAL, not a number spliced into a sentence. English is invariant across all six
            // forms -- "the 1-day recovery period" and "the 30-day recovery period" are both
            // correct -- and Russian is not: "в течение 1 дня" against "в течение 30 дней", with
            // the form governed by the preposition rather than by the bare numeral. The count is
            // passed twice on purpose, once to choose the form and once to be printed.
            text = pluralStringResource(
                Res.plurals.profile_settings_deletion_recovery,
                deletion.recoveryDays,
                deletion.recoveryDays,
            ),
            style = MaterialTheme.typography.bodySmall,
            color = colors.textSecondary,
        )
        Spacer(Modifier.height(Space.two))
        // Not destructive: this is the button that STOPS the destructive thing, and painting it
        // red would put the warning colour on the safe action.
        SwetSecondaryButton(
            text = stringResource(Res.string.profile_settings_keep_account),
            onClick = onCancel,
            enabled = !busy,
        )
    }
}
